import React, { useState, useEffect } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Card,
    CardBody,
    Row,
    Col,
    Spinner,
    Input,
    Label,
} from "reactstrap";
import { toast } from "react-toastify";
import { Image as ImageIcon, Upload, X, Save, GripVertical } from "lucide-react";
import { useMenuItemsStore } from "../../../stores/Assets/menuItems";

const HandleImagesModal = ({ isOpen, toggle, menuId, imageUrl }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const { updateItem } = useMenuItemsStore();

    useEffect(() => {
        if (isOpen && menuId) {
            fetchAllItems();
        }
    }, [isOpen, menuId]);

    const fetchAllItems = async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/menu/${menuId}/items?page=1&perPage=1000`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch menu items");
            }

            const data = await response.json();
            setItems(data.items.map(item => ({
                ...item,
                item_id: item.menu_item_id, // Map it for consistency
                stagedImage: null,
                previewUrl: null
            })));
        } catch (error) {
            console.error("Error fetching menu items:", error);
            toast.error("Failed to load menu items");
        } finally {
            setLoading(false);
        }
    };

    const calculateSimilarity = (itemName, fileName) => {
        const normalizedItem = itemName.toLowerCase().replace(/[^a-z0-9]/g, '');
        // Remove last extension (everything from the last dot to the end)
        const fileNameWithoutExtension = fileName.toLowerCase().replace(/\.[^/.]+$/, "");
        const normalizedFile = fileNameWithoutExtension.replace(/[^a-z0-9]/g, '');

        const itemChars = normalizedItem.split('');
        const fileChars = normalizedFile.split('');

        let matches = 0;
        const tempItemChars = [...itemChars];
        for (const char of fileChars) {
            const index = tempItemChars.indexOf(char);
            if (index !== -1) {
                matches++;
                tempItemChars.splice(index, 1);
            }
        }
        return matches;
    };

    const handleImportImages = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const allMatches = [];

        // 1. Calculate all possible scores
        files.forEach((file, fileIndex) => {
            items.forEach((item, itemIndex) => {
                const score = calculateSimilarity(item.title, file.name);
                if (score > 0) {
                    allMatches.push({ fileIndex, itemIndex, score, file, item });
                }
            });
        });

        // 2. Sort matches by score DESC
        // Tie-breaker: prefer match where length difference is smaller
        allMatches.sort((a, b) => {
            if (b.score !== a.score) return b.score - a.score;
            const normalizedFileNameA = a.file.name.replace(/\.[^/.]+$/, "").replace(/[^a-z0-9]/g, '').length;
            const normalizedItemNameA = a.item.title.replace(/[^a-z0-9]/g, '').length;
            const diffA = Math.abs(normalizedFileNameA - normalizedItemNameA);

            const normalizedFileNameB = b.file.name.replace(/\.[^/.]+$/, "").replace(/[^a-z0-9]/g, '').length;
            const normalizedItemNameB = b.item.title.replace(/[^a-z0-9]/g, '').length;
            const diffB = Math.abs(normalizedFileNameB - normalizedItemNameB);

            return diffA - diffB;
        });

        // 3. Assign greedily
        const assignedFiles = new Set();
        const assignedItems = new Set();
        const updatedItems = items.map(item => ({ ...item }));

        allMatches.forEach(match => {
            if (!assignedFiles.has(match.fileIndex) && !assignedItems.has(match.itemIndex)) {
                updatedItems[match.itemIndex].stagedImage = match.file;
                updatedItems[match.itemIndex].previewUrl = URL.createObjectURL(match.file);
                assignedFiles.add(match.fileIndex);
                assignedItems.add(match.itemIndex);
            }
        });

        setItems(updatedItems);
        if (assignedItems.size > 0) {
            toast.success(`Automated matching completed. ${assignedItems.size} items matched.`);
        } else {
            toast.warning("No matches found. Try naming your images closer to item titles.");
        }
    };

    const handleSave = async () => {
        const itemsToUpdate = items.filter(item => item.stagedImage);
        if (itemsToUpdate.length === 0) {
            toast.warning("No new images to save.");
            return;
        }

        try {
            setSaving(true);
            for (const item of itemsToUpdate) {
                const formData = new FormData();
                formData.append("newImages", item.stagedImage);
                // We use v2 updateItem which expects FormData for images
                await updateItem(menuId, item.item_id || item.menu_item_id, formData);
            }
            toast.success("All images updated successfully!");
            toggle();
        } catch (error) {
            console.error("Error updating images:", error);
            toast.error("Failed to update some images.");
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveStaged = (itemId) => {
        setItems(items.map(item => {
            if (item.item_id === itemId) {
                if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
                return { ...item, stagedImage: null, previewUrl: null };
            }
            return item;
        }));
    };

    const handleDragStart = (e, sourceItemId) => {
        const sourceItem = items.find(i => i.item_id === sourceItemId);
        if (!sourceItem.stagedImage && (!sourceItem.images || sourceItem.images.length === 0)) {
            // If no image to drag, do nothing
            return;
        }
        e.dataTransfer.setData("sourceItemId", sourceItemId);
    };

    const handleDrop = (e, targetItemId) => {
        e.preventDefault();
        const sourceItemId = parseInt(e.dataTransfer.getData("sourceItemId"));
        if (sourceItemId === targetItemId) return;

        const sourceIndex = items.findIndex(i => i.item_id === sourceItemId);
        const targetIndex = items.findIndex(i => i.item_id === targetItemId);

        if (sourceIndex === -1 || targetIndex === -1) return;

        const newItems = [...items];
        const sourceItem = newItems[sourceIndex];
        const targetItem = newItems[targetIndex];

        // Swap staged images and previews
        const tempStaged = sourceItem.stagedImage;
        const tempPreview = sourceItem.previewUrl;

        sourceItem.stagedImage = targetItem.stagedImage;
        sourceItem.previewUrl = targetItem.previewUrl;

        targetItem.stagedImage = tempStaged;
        targetItem.previewUrl = tempPreview;

        setItems(newItems);
    };

    const filteredItems = items.filter(item =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getItemImage = (item) => {
        if (item.previewUrl) return item.previewUrl;
        if (item.images && item.images.length > 0) return imageUrl + item.images[0];
        return null;
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
            <ModalHeader toggle={toggle}>
                <div className="d-flex align-items-center gap-2">
                    <ImageIcon size={20} />
                    Handle Images
                </div>
            </ModalHeader>

            <ModalBody>
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2 text-muted">Loading menu items...</p>
                    </div>
                ) : (
                    <>
                        <div className="mb-4 d-flex flex-column flex-md-row gap-3 align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-2 w-100" style={{ maxWidth: "400px" }}>
                                <Label className="mb-0 text-nowrap">Search Items:</Label>
                                <Input
                                    type="text"
                                    placeholder="Filter items..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            <div>
                                <Button color="primary" onClick={() => document.getElementById('bulk-image-upload').click()}>
                                    <Upload size={16} className="me-2" />
                                    Import Images
                                </Button>
                                <input
                                    type="file"
                                    id="bulk-image-upload"
                                    multiple
                                    accept="image/*"
                                    className="d-none"
                                    onChange={handleImportImages}
                                />
                            </div>
                        </div>

                        <Row className="g-3">
                            {filteredItems.map((item) => (
                                <Col key={item.item_id} xs={12} sm={6} md={4} lg={3}>
                                    <Card
                                        className={`h-100 ${item.stagedImage ? 'border-primary' : ''}`}
                                        draggable={!!item.stagedImage || (item.images && item.images.length > 0)}
                                        onDragStart={(e) => handleDragStart(e, item.item_id)}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => handleDrop(e, item.item_id)}
                                    >
                                        <CardBody className="p-2 d-flex flex-column">
                                            <div
                                                className="position-relative bg-light rounded overflow-hidden mb-2"
                                                style={{ aspectRatio: "4/3" }}
                                            >
                                                {getItemImage(item) ? (
                                                    <img
                                                        src={getItemImage(item)}
                                                        alt={item.title}
                                                        className="w-100 h-100"
                                                        style={{ objectFit: "cover" }}
                                                    />
                                                ) : (
                                                    <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted">
                                                        <ImageIcon size={40} opacity={0.3} />
                                                    </div>
                                                )}

                                                {item.stagedImage && (
                                                    <div className="position-absolute top-0 end-0 p-1">
                                                        <Button
                                                            color="danger"
                                                            size="sm"
                                                            className="rounded-circle p-1"
                                                            onClick={() => handleRemoveStaged(item.item_id)}
                                                        >
                                                            <X size={14} />
                                                        </Button>
                                                    </div>
                                                )}

                                                {item.stagedImage && (
                                                    <div className="position-absolute bottom-0 start-0 w-100 bg-primary text-white text-center py-1" style={{ fontSize: "10px", opacity: 0.9 }}>
                                                        STAGED (MATCHED)
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex-grow-1">
                                                <h6 className="mb-0 text-truncate" title={item.title}>{item.title}</h6>
                                                {item.stagedImage && (
                                                    <small className="text-primary text-truncate d-block" style={{ fontSize: "10px" }}>
                                                        from: {item.stagedImage.name}
                                                    </small>
                                                )}
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </>
                )}
            </ModalBody>

            <ModalFooter>
                <Button color="secondary" onClick={toggle} disabled={saving}>
                    Cancel
                </Button>
                <Button color="success" onClick={handleSave} disabled={saving || items.filter(i => i.stagedImage).length === 0}>
                    {saving ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <Save size={16} className="me-2" />
                            Save {items.filter(i => i.stagedImage).length} Changes
                        </>
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default HandleImagesModal;
